import { AnimatedSizeContainer, Table } from "@/components";
import { EventStatus, EventType } from "@/types";
import {
  calculateAge,
  formatDate,
  formatEventType,
  formatUserRoleToName,
  ImgSrc,
} from "@/utils";
import { LocationOn } from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  CardContent,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import { FC, useMemo } from "react";
import { Loading } from "react-admin";
import { PhotoProvider, PhotoView } from "react-photo-view";
import { useNavigate, useParams } from "react-router-dom";
import { useGetEvent } from "../../queries";
import { useUIStore } from "@/stores";
import { CancelEvent } from "../../modals";
import dayjs from "dayjs";

const EventsShow: FC = () => {
  const { id } = useParams();
  const { openModal } = useUIStore();
  const navigate = useNavigate();

  const { event, isEventLoading } = useGetEvent(id ?? "");

  const imageAttachments = useMemo(() => {
    if (!event) return;

    if (event.type === EventType.DATE_NIGHT && event.creator.profileImage) {
      return [event.creator.profileImage];
    }
    if (event.type === EventType.AFFAIR && event.photos?.length) {
      return event.photos;
    }
  }, [event]);

  // These restrictions described in api docs
  const isCancellationPossible = useMemo(() => {
    if (!event) return false;
    if (event.status !== EventStatus.ACTIVE || event.isExpired) {
      return false;
    }

    const now = dayjs();
    if (event.type === EventType.DATE_NIGHT && event.date) {
      const eventDate = dayjs(event.date);
      return now.diff(eventDate, "day") <= 1;
    }
    if (event.type === EventType.AFFAIR && event.endDate) {
      const eventEndDate = dayjs(event.endDate);
      return now.diff(eventEndDate, "day") <= 1;
    }

    return false;
  }, [event]);

  if (isEventLoading) {
    return <Loading />;
  }

  return (
    <AnimatedSizeContainer height>
      <Stack direction="row">
        {imageAttachments && (
          <CardContent>
            <PhotoProvider>
              <PhotoView src={ImgSrc(imageAttachments[0].path)}>
                <img
                  style={{
                    maxHeight: "50vh",
                    maxWidth: "300px",
                    borderRadius: 4,
                    cursor: "pointer",
                  }}
                  src={ImgSrc(imageAttachments?.[0]?.path ?? "")}
                />
              </PhotoView>
              {imageAttachments.toSpliced(0, 1).map(({ path }) => (
                <PhotoView key={path} src={ImgSrc(path)} />
              ))}
            </PhotoProvider>
          </CardContent>
        )}

        <CardContent
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Chip
              label={formatEventType(event.type)}
              color="primary"
              sx={{ width: "fit-content" }}
            />
            {event.status === EventStatus.ACTIVE ? (
              <Button
                disabled={!isCancellationPossible}
                onClick={() =>
                  openModal(CancelEvent, {
                    title: "Event cancellation",
                    eventId: event.id,
                  })
                }
              >
                Cancel Event
              </Button>
            ) : (
              <Typography color="error">Event Cancelled</Typography>
            )}
          </Stack>

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="h5" fontWeight="bold">
                {event.title}
              </Typography>

            </Stack>
            <Stack
              direction={"row"}
              alignItems={"center"}
              spacing={1}
              onClick={() => navigate(`/users/${event.creator.id}/show`)}
              sx={{ cursor: "pointer" }}
            >
              <Avatar
                alt="avatar"
                src={ImgSrc(event.creator.profileImage?.path ?? "")}
                sx={{ height: 32, width: 32 }}
              />
              <Typography variant="body2">{`${event.creator.firstName} ${event.creator.lastName}`}</Typography>
            </Stack>
          </Stack>

          <Typography variant="body2" color="text.secondary">
            Description: {event.description}
          </Typography>

          {event.startDate && event.endDate ? (
            <Typography variant="body2" color="text.secondary">
              {formatDate(event.startDate ?? "")} –{" "}
              {formatDate(event.endDate ?? "")}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {formatDate(event.date ?? "")}
            </Typography>
          )}

          <Box display="flex" alignItems="center">
            <LocationOn
              fontSize="small"
              sx={{ color: "text.secondary", mr: 0.5 }}
            />
            <Typography variant="body2" color="text.secondary">
              {event.location.label}
            </Typography>
          </Box>

          <Stack>
            <Typography variant="body2" color="text.secondary">
              Participants: {event.participantsCount}{" "}
              {event.capacity ? `(capacity: ${event.capacity})` : ""}
            </Typography>

            {event.participants.length !== 0 && (
              <Table
                page={-1}
                rowsPerPage={-1}
                rowsPerPageOptions={[]}
                headCells={["Name", "Role", "Age"]}
                count={event.participants.length}
                onPageChange={() => null}
                bodyCells={event.participants.map(
                  ({
                    id,
                    firstName,
                    lastName,
                    profileImage,
                    role,
                    dateOfBirth,
                  }) => [
                    <Stack
                      direction={"row"}
                      alignItems={"center"}
                      spacing={1}
                      onClick={() => navigate(`/users/${id}/show`)}
                    >
                      <Avatar
                        alt="avatar"
                        src={ImgSrc(profileImage?.path ?? "")}
                        sx={{ height: 32, width: 32 }}
                      />
                      <Typography variant="body2">{`${firstName} ${lastName}`}</Typography>
                    </Stack>,
                    formatUserRoleToName(role),
                    <Typography variant="body2">
                      {calculateAge(dateOfBirth)}
                    </Typography>,
                  ]
                )}
              />
            )}
          </Stack>

          <Typography
            variant="body2"
            color={event.isExpired ? "error" : "success"}
          >
            {event.isExpired
              ? "This event has expired."
              : "This event hasn't expired."}
          </Typography>
        </CardContent>
      </Stack>
    </AnimatedSizeContainer>
  );
};

export { EventsShow };
